/**
 * Lean neighbor physics, vendored from the sim's NeighborField: every drone
 * seeks its target slot while pushing smoothly off any neighbor inside the
 * safe radius, so no two ever touch. Spatial hash keeps separation ~O(n).
 * Pure module (no React, no three) so it can be unit-tested by import.
 */

const TABLE = 1 << 14;
const MASK = TABLE - 1;
const MAX_NEIGH = 48;

function hashCell(cx: number, cy: number, cz: number) {
  return (((cx * 92837111) ^ (cy * 689287499) ^ (cz * 283923481)) & MASK) >>> 0;
}

/**
 * A body the swarm must avoid: the pilot's own drone cursor. It carries its
 * own safe zone, and drones inside it are allowed a HIGHER speed cap than
 * the formed swarm, because at the settled cap (10 lu/s) they simply could
 * not clear a cursor crossing the screen. A real drone getting buzzed moves.
 */
export type Obstacle = {
  x: number;
  y: number;
  z: number;
  r: number; // safe zone, scene units
  push: number; // peak acceleration at the centre, falling to 0 at r
  speed: number; // escape speed cap for drones inside r, scene units/s
  /** Unit heading, when moving: drones step ASIDE from it rather than ahead. */
  ux: number;
  uy: number;
  uz: number;
  moving: boolean;
};

export type SimParams = {
  safe: number; // min comfortable center-to-center distance (scene units)
  maxSpeed: number; // scene units / second
  rigidity: number; // seek-to-slot spring strength
  avoidance: number; // separation force strength
  damping: number; // velocity damping
};

export class Swarm {
  readonly n: number;
  pos: Float32Array;
  vel: Float32Array;
  tgt: Float32Array;
  private cellHead = new Int32Array(TABLE);
  private next: Int32Array;

  constructor(positions: Float32Array) {
    this.n = positions.length / 3;
    this.pos = positions.slice();
    this.vel = new Float32Array(positions.length);
    this.tgt = positions.slice();
    this.next = new Int32Array(this.n);
  }

  /**
   * `rad`, when given, is a PER-DRONE safe radius (scene units) and replaces
   * p.safe: a pair's threshold is the mean of the two radii, so one drone
   * opening up shoves its neighbours while they hold their own spacing.
   * That is what lets a wavefront cross the swarm.
   */
  step(dtRaw: number, p: SimParams, rad?: Float32Array, ob?: Obstacle) {
    const dt = Math.min(dtRaw, 1 / 30);
    const { pos, vel, tgt, cellHead, next, n } = this;
    const safe = Math.max(p.safe, 1e-3);
    const maxSp = p.maxSpeed;
    const damp = Math.max(0, 1 - p.damping * dt);
    // cells are sized to the LARGEST radius in play, so no inflated pair is
    // ever missed by the 27-cell neighbour scan
    let maxRad = safe;
    if (rad) for (let i = 0; i < n; i++) if (rad[i] > maxRad) maxRad = rad[i];
    const inv = 1 / maxRad;

    cellHead.fill(-1);
    for (let i = 0; i < n; i++) {
      const h = hashCell(
        Math.floor(pos[i * 3] * inv),
        Math.floor(pos[i * 3 + 1] * inv),
        Math.floor(pos[i * 3 + 2] * inv)
      );
      next[i] = cellHead[h];
      cellHead[h] = i;
    }

    for (let i = 0; i < n; i++) {
      const ix = i * 3, iy = ix + 1, iz = ix + 2;
      const px = pos[ix], py = pos[iy], pz = pos[iz];
      const radI = rad ? rad[i] : safe;

      let ax = (tgt[ix] - px) * p.rigidity;
      let ay = (tgt[iy] - py) * p.rigidity;
      let az = (tgt[iz] - pz) * p.rigidity;

      const cx = Math.floor(px * inv), cy = Math.floor(py * inv), cz = Math.floor(pz * inv);
      let visited = 0;
      for (let dx = -1; dx <= 1 && visited < MAX_NEIGH; dx++)
        for (let dy = -1; dy <= 1 && visited < MAX_NEIGH; dy++)
          for (let dz = -1; dz <= 1 && visited < MAX_NEIGH; dz++) {
            let j = cellHead[hashCell(cx + dx, cy + dy, cz + dz)];
            while (j >= 0 && visited < MAX_NEIGH) {
              if (j !== i) {
                visited++;
                const rx = px - pos[j * 3], ry = py - pos[j * 3 + 1], rz = pz - pos[j * 3 + 2];
                const d2 = rx * rx + ry * ry + rz * rz;
                const thresh = rad ? (radI + rad[j]) * 0.5 : safe;
                if (d2 < thresh * thresh && d2 > 1e-9) {
                  const dist = Math.sqrt(d2);
                  const f = (((thresh - dist) / thresh) * p.avoidance) / dist;
                  ax += rx * f;
                  ay += ry * f;
                  az += rz * f;
                }
              }
              j = next[j];
            }
          }

      // the pilot's drone: get clear of it, and be quick about it
      let cap = maxSp;
      if (ob) {
        const rx = px - ob.x, ry = py - ob.y, rz = pz - ob.z;
        const d2 = rx * rx + ry * ry + rz * rz;
        if (d2 < ob.r * ob.r) {
          cap = ob.speed;
          // Step ASIDE, not away. Pushing straight down the radial shoves
          // whatever is ahead of the cursor further ahead, so it gathers a
          // bow wave and bulldozes drones across the sign. Dropping the
          // component along the cursor's heading makes them clear its path
          // instead, which is what avoidance actually looks like. A parked
          // cursor has no heading, so there it opens a plain round hole.
          let dx = rx, dy = ry, dz = rz;
          if (ob.moving) {
            const dot = rx * ob.ux + ry * ob.uy + rz * ob.uz;
            dx -= dot * ob.ux;
            dy -= dot * ob.uy;
            dz -= dot * ob.uz;
          }
          let dl = Math.sqrt(dx * dx + dy * dy + dz * dz);
          if (dl < 1e-6) { dx = 0; dy = 1; dz = 0; dl = 1; } // dead on the axis
          const f = (1 - Math.sqrt(d2) / ob.r) * ob.push;
          ax += (dx / dl) * f;
          ay += (dy / dl) * f;
          az += (dz / dl) * f;
        }
      }

      let vx = (vel[ix] + ax * dt) * damp;
      let vy = (vel[iy] + ay * dt) * damp;
      let vz = (vel[iz] + az * dt) * damp;
      const v2 = vx * vx + vy * vy + vz * vz;
      if (v2 > cap * cap) {
        const s = cap / Math.sqrt(v2);
        vx *= s; vy *= s; vz *= s;
      }
      vel[ix] = vx; vel[iy] = vy; vel[iz] = vz;
      pos[ix] = px + vx * dt;
      pos[iy] = py + vy * dt;
      pos[iz] = pz + vz * dt;
    }
  }
}
